import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {
    Add as AddIcon,
    ArrowDownward as ArrowDownwardIcon,
    ArrowUpward as ArrowUpwardIcon,
    DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";

let rowSeq = 0;
// A row needs a React key that survives reordering and relabelling; a choice's
// id would do, but a new row has none yet.
const row = (choice = null) => ({ key: `row-${(rowSeq += 1)}`, id: choice?.id ?? null, label: choice?.label || "" });

// Create or edit a SEGMENT: its title and its ordered list of choices, in one
// dialog. This is the whole of what used to take two record types and two
// forms (an Area dialog and a Group drawer): a segment IS a title and its
// choices, so both are edited together.
//
// Saving sends the full ordered list. A choice that keeps its id is relabelled
// in place, so impacts holding it keep it; one removed here is removed from
// every impact — which is why its row says how many before you save.
export default function SegmentForm({
    open,
    // The segment being edited; absent when creating.
    segment = null,
    // choice id -> impacts holding it, so removing a choice can say what it costs.
    usage = {},
    onSubmit,
    onCancel,
    saving = false,
    // The server's message (a duplicate title, most often), shown under the title.
    error = null,
}) {
    const intl = useIntl();
    const isEdit = Boolean(segment);
    const [title, setTitle] = useState("");
    const [rows, setRows] = useState([]);
    const [touched, setTouched] = useState(false);

    // Re-seed each time it opens, so a create never inherits the last draft.
    React.useEffect(() => {
        if (!open) return;
        setTitle(segment?.title || "");
        setRows(segment?.choices?.length ? segment.choices.map(row) : [row(), row()]);
        setTouched(false);
    }, [open, segment]);

    const trimmedTitle = title.trim();
    const filled = rows.filter((r) => r.label.trim());
    const seen = new Set();
    const duplicates = new Set();
    for (const r of filled) {
        const key = r.label.trim().toLowerCase();
        if (seen.has(key)) duplicates.add(key);
        seen.add(key);
    }
    const titleMissing = touched && !trimmedTitle;
    const choicesMissing = touched && filled.length === 0;
    const invalid = !trimmedTitle || filled.length === 0 || duplicates.size > 0;

    const setLabel = (key, label) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, label } : r)));
    const remove = (key) => setRows((prev) => prev.filter((r) => r.key !== key));
    const move = (index, delta) =>
        setRows((prev) => {
            const next = [...prev];
            const [moved] = next.splice(index, 1);
            next.splice(index + delta, 0, moved);
            return next;
        });

    const submit = (e) => {
        e.preventDefault();
        setTouched(true);
        if (invalid) return;
        onSubmit({
            title: trimmedTitle,
            // Blank rows are dropped rather than refused: an empty trailing row
            // is what "Add choice" leaves behind, not a mistake.
            choices: filled.map((r) => ({ ...(r.id != null ? { id: r.id } : {}), label: r.label.trim() })),
        });
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="sm" fullWidth>
            <form onSubmit={submit}>
                <DialogTitle>
                    {isEdit ? (
                        <FormattedMessage id="impact.edit_segment" defaultMessage="Edit segment" />
                    ) : (
                        <FormattedMessage id="impact.add_segment" defaultMessage="Add segment" />
                    )}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        error={titleMissing || Boolean(error)}
                        label={intl.formatMessage({ id: "impact.segment_title", defaultMessage: "Segment title" })}
                        helperText={
                            error ||
                            (titleMissing
                                ? intl.formatMessage({
                                      id: "impact.segment_title_required",
                                      defaultMessage: "A segment needs a title.",
                                  })
                                : intl.formatMessage({
                                      id: "impact.segment_title_help",
                                      defaultMessage: "How you slice the register — for example Region, Function or Seniority.",
                                  }))
                        }
                    />

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                        <FormattedMessage id="impact.segment_choices" defaultMessage="Choices" />
                    </Typography>
                    <Stack spacing={1}>
                        {rows.map((r, index) => {
                            const duplicate = duplicates.has(r.label.trim().toLowerCase());
                            const used = r.id != null ? usage[String(r.id)] || 0 : 0;
                            return (
                                <Stack key={r.key} direction="row" spacing={0.5} alignItems="flex-start">
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={r.label}
                                        onChange={(e) => setLabel(r.key, e.target.value)}
                                        error={duplicate}
                                        inputProps={{
                                            "aria-label": intl.formatMessage(
                                                { id: "impact.segment_choice_n", defaultMessage: "Choice {n}" },
                                                { n: index + 1 }
                                            ),
                                        }}
                                        helperText={
                                            duplicate
                                                ? intl.formatMessage({
                                                      id: "impact.segment_choice_duplicate",
                                                      defaultMessage: "Listed twice.",
                                                  })
                                                : undefined
                                        }
                                    />
                                    <IconButton
                                        size="small"
                                        disabled={index === 0}
                                        onClick={() => move(index, -1)}
                                        aria-label={intl.formatMessage({ id: "impact.move_up", defaultMessage: "Move up" })}
                                    >
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        disabled={index === rows.length - 1}
                                        onClick={() => move(index, 1)}
                                        aria-label={intl.formatMessage({ id: "impact.move_down", defaultMessage: "Move down" })}
                                    >
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => remove(r.key)}
                                        // Says what removing it costs, in the name a
                                        // screen reader hears and the tooltip shows.
                                        title={
                                            used
                                                ? intl.formatMessage(
                                                      {
                                                          id: "impact.segment_choice_in_use",
                                                          defaultMessage:
                                                              "Used by {count, plural, one {# impact} other {# impacts}} — they will be left unset",
                                                      },
                                                      { count: used }
                                                  )
                                                : undefined
                                        }
                                        aria-label={intl.formatMessage(
                                            { id: "impact.remove_choice", defaultMessage: "Remove {choice}" },
                                            { choice: r.label || index + 1 }
                                        )}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            );
                        })}
                    </Stack>
                    {choicesMissing && (
                        <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 1 }}>
                            <FormattedMessage
                                id="impact.segment_choices_required"
                                defaultMessage="Add at least one choice."
                            />
                        </Typography>
                    )}
                    <Box sx={{ mt: 1 }}>
                        <Button
                            size="small"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={() => setRows((prev) => [...prev, row()])}
                            sx={{ color: "#00438E", textTransform: "none" }}
                        >
                            <FormattedMessage id="impact.add_choice" defaultMessage="Add choice" />
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onCancel} disabled={saving} sx={{ textTransform: "none" }}>
                        <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={saving || (touched && invalid)}
                        sx={{
                            textTransform: "none",
                            backgroundColor: "#00438E",
                            "&:hover": { backgroundColor: "#003A75" },
                        }}
                    >
                        <FormattedMessage id="general.save" defaultMessage="Save" />
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
